import { useState, useEffect, useRef } from "react";
import { Scan, Loader2, X, AlertCircle, Shield, ExternalLink, Share2, Copy, Check, Twitter, Facebook, ImagePlus, Upload, Type, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";

// Top-level helper — kept outside the component so `react-hooks/immutability`
// doesn't flag the DOM FileList read inside the React render scope.
function firstFileFromEvent(e, source) {
  const obj = source === "dataTransfer" ? e.dataTransfer : e.target;
  if (!obj) return null;
  const files = obj.files;
  return files && files.length > 0 ? files[0] : null;
}
import { http } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import PricingModal from "./PricingModal";

const MEDIA_LABELS = {
  twitter: "X / Twitter", youtube: "YouTube", reddit: "Reddit", tiktok: "TikTok",
  facebook: "Facebook", instagram: "Instagram", threads: "Threads", bluesky: "Bluesky",
  linkedin: "LinkedIn", podcast: "Podcast", substack: "Substack", blog: "Blog",
  news: "News article", other: "Web page", text: "Text", image: "Screenshot",
};

/**
 * Zero-friction Universal Scorer chip.
 * Lives in the header functionality bar. Click → modal opens with paste-URL
 * input. Result + scores render inline without leaving the page. Wired to the
 * same /reader/paste-analyze endpoint as /reader-tools.
 */
export default function UniversalScorerWidget() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="func-link font-black text-[var(--gold)] hover:text-white hover:bg-[var(--gold)] transition border border-[var(--gold)] rounded-full px-2 py-1"
        data-testid="header-score-url"
        title="Score any URL — news, X, YouTube, Reddit"
      >
        <Scan size={12} /> Score any URL
      </button>
      {open && <UniversalScorerModal onClose={() => setOpen(false)} />}
    </>
  );
}

function UniversalScorerModal({ onClose }) {
  const { token } = useAuth();
  // Input modes: 'url' (paste URL), 'image' (upload screenshot), 'text' (paste content)
  const [mode, setMode] = useState("url");
  const [url, setUrl] = useState("");
  const [pastedText, setPastedText] = useState("");
  const [pastedTitle, setPastedTitle] = useState("");
  const [imageBase64, setImageBase64] = useState("");
  const [imageMime, setImageMime] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [imageFilename, setImageFilename] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [showPricing, setShowPricing] = useState(false);
  const [copied, setCopied] = useState(false);
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const shareUrl = result?.share_id ? `${apiBase}/api/share/scored/${result.share_id}` : "";
  const fileInputRef = useRef(null);

  // ESC closes + body scroll lock
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Paste-from-clipboard: if the user pastes an image (Cmd/Ctrl+V) while the
  // modal is open, auto-switch to image mode and load the image
  useEffect(() => {
    const onPaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith("image/")) {
          const file = items[i].getAsFile();
          if (file) {
            e.preventDefault();
            await loadImageFile(file);
            setMode("image");
            return;
          }
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  const loadImageFile = async (file) => {
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowed.includes(file.type)) {
      setErr("Unsupported image format. Use PNG, JPEG, or WEBP.");
      setTimeout(() => setErr(""), 4000);
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setErr("Image too large (8 MB max).");
      setTimeout(() => setErr(""), 4000);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const b64 = String(dataUrl).split(",")[1] || "";
      setImageBase64(b64);
      setImageMime(file.type);
      setImagePreview(String(dataUrl));
      setImageFilename(file.name || "screenshot");
    };
    reader.readAsDataURL(file);
  };

  const onFilePicked = async (e) => {
    const f = firstFileFromEvent(e, "target");
    if (f) await loadImageFile(f);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = firstFileFromEvent(e, "dataTransfer");
    if (f) {
      await loadImageFile(f);
      setMode("image");
    }
  };

  const clearImage = () => {
    setImageBase64(""); setImageMime(""); setImagePreview(""); setImageFilename("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const copy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch { /* ignore */ }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setErr(""); setResult(null);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const payload = {};
      if (mode === "url") {
        if (!url.trim()) { setBusy(false); return; }
        payload.url = url.trim();
      } else if (mode === "image") {
        if (!imageBase64) { setBusy(false); setErr("Drop, choose, or paste a screenshot first."); return; }
        payload.image_base64 = imageBase64;
        payload.image_mime = imageMime;
        if (pastedTitle.trim()) payload.title = pastedTitle.trim();
      } else if (mode === "text") {
        if (!pastedText.trim()) { setBusy(false); return; }
        payload.text = pastedText.trim();
        if (pastedTitle.trim()) payload.title = pastedTitle.trim();
      }
      const r = await http.post("/reader/paste-analyze", payload, { headers });
      setResult(r.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not analyze. Try a different input.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/65 z-50 flex items-start sm:items-center justify-center p-0 sm:p-4 backdrop-blur-sm" onClick={onClose} data-testid="universal-scorer-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-[720px] w-full max-h-[92vh] sm:max-h-[88vh] overflow-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-[#0a1f3d] to-[#1e3a8a] text-white p-4 sm:p-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-md bg-[var(--gold)] text-[#1d1303]">
              <Scan size={16} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[var(--gold)]">Newseze Universal Scorer</div>
              <div className="text-[12.5px] font-bold opacity-90">URL · screenshot · pasted text — anywhere, any format</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-11 h-11 -mr-2 flex items-center justify-center rounded-full hover:bg-white/15 active:bg-white/25 transition"
            aria-label="Close"
            data-testid="universal-scorer-close"
          >
            <X size={22} strokeWidth={2.5} />
          </button>
        </div>

        {/* Mode tabs */}
        <div className="border-b border-[var(--line)] flex" data-testid="scorer-mode-tabs">
          {[
            { id: "url",   icon: LinkIcon,   label: "URL"        },
            { id: "image", icon: ImagePlus,  label: "Screenshot" },
            { id: "text",  icon: Type,       label: "Paste text" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => { setMode(t.id); setResult(null); setErr(""); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-[11.5px] uppercase tracking-[0.16em] font-black border-b-2 transition ${mode === t.id ? "text-[var(--blue)] border-[var(--gold)] bg-[#fff9ed]" : "text-[var(--muted)] border-transparent hover:text-[var(--ink)]"}`}
              data-testid={`scorer-mode-${t.id}`}
            >
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>

        {/* Form */}
        <div className="p-4 sm:p-5">
          <form onSubmit={submit} className="mb-4">
            {mode === "url" && (
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://x.com/… · youtube.com/watch?v=… · reuters.com/… · reddit.com/r/…"
                  className="flex-1 border border-[var(--line)] rounded-md px-3 py-2.5 text-[13px]"
                  data-testid="universal-scorer-url"
                />
                <button type="submit" disabled={busy} className="btn primary justify-center" data-testid="universal-scorer-submit">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Scan size={14} />}
                  {busy ? "Scoring…" : "Score it"}
                </button>
              </div>
            )}

            {mode === "image" && (
              <div className="space-y-3">
                {!imagePreview ? (
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition ${dragOver ? "border-[var(--gold)] bg-[#fff9ed]" : "border-[var(--line)] hover:border-[var(--gold)] hover:bg-[#fff9ed]"}`}
                    data-testid="scorer-image-dropzone"
                  >
                    <Upload size={28} className="mx-auto text-[var(--gold)] mb-2" />
                    <div className="text-[14px] font-black mb-1">Drop a screenshot here</div>
                    <div className="text-[11.5px] text-[var(--muted)] mb-3">…or click to choose a file · or press <b>Cmd/Ctrl+V</b> to paste from clipboard</div>
                    <div className="text-[10.5px] text-[var(--muted)]">PNG · JPEG · WEBP · max 8 MB</div>
                    <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFilePicked} className="hidden" data-testid="scorer-image-file" />
                  </div>
                ) : (
                  <div className="rounded-xl border border-[var(--gold)] overflow-hidden" data-testid="scorer-image-preview">
                    <div className="bg-[var(--bg)] px-3 py-2 flex items-center justify-between text-[11px]">
                      <span className="font-black truncate flex items-center gap-1.5"><ImagePlus size={12} /> {imageFilename}</span>
                      <button type="button" onClick={clearImage} className="text-[var(--muted)] hover:text-rose-600 font-black" data-testid="scorer-image-clear">Remove</button>
                    </div>
                    <img src={imagePreview} alt="Preview" className="w-full max-h-[260px] object-contain bg-black/5" />
                  </div>
                )}
                <input
                  type="text"
                  value={pastedTitle}
                  onChange={(e) => setPastedTitle(e.target.value)}
                  placeholder="Optional context: 'X post by @account' or 'Article from Reuters'"
                  className="w-full border border-[var(--line)] rounded-md px-3 py-2 text-[12.5px]"
                  data-testid="scorer-image-context"
                />
                <button type="submit" disabled={busy || !imageBase64} className="btn primary w-full justify-center" data-testid="universal-scorer-submit-image">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Scan size={14} />}
                  {busy ? "Reading screenshot…" : "Score this screenshot"}
                </button>
              </div>
            )}

            {mode === "text" && (
              <div className="space-y-2">
                <input
                  type="text"
                  value={pastedTitle}
                  onChange={(e) => setPastedTitle(e.target.value)}
                  placeholder="Headline or context (optional)"
                  className="w-full border border-[var(--line)] rounded-md px-3 py-2 text-[13px]"
                  data-testid="scorer-text-title"
                />
                <textarea
                  value={pastedText}
                  onChange={(e) => setPastedText(e.target.value)}
                  placeholder="Paste the content — a tweet, an article excerpt, a Reddit thread, anything. Newseze will score it."
                  rows={6}
                  maxLength={6000}
                  className="w-full border border-[var(--line)] rounded-md px-3 py-2 text-[13px] resize-y"
                  data-testid="scorer-text-body"
                />
                <button type="submit" disabled={busy || !pastedText.trim()} className="btn primary w-full justify-center" data-testid="universal-scorer-submit-text">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Scan size={14} />}
                  {busy ? "Scoring…" : "Score this content"}
                </button>
              </div>
            )}
          </form>

          <div className="text-[11px] text-[var(--muted)] italic mb-3">
            Free: 1 score per day · Reader ($6.99): 10/day · Pro: 30/day · Channels &amp; Elite: unlimited.
          </div>

          {err && (
            <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-[12.5px] text-rose-800 flex items-start gap-2 mb-3" data-testid="universal-scorer-error">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              <div className="flex-1">
                {err}
                {/limit reached|upgrade/i.test(err) && (
                  <button onClick={() => setShowPricing(true)} className="block mt-1.5 text-[var(--blue)] font-black underline" data-testid="universal-scorer-upgrade-cta">
                    See plans →
                  </button>
                )}
              </div>
            </div>
          )}

          {result && (
            <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4" data-testid="universal-scorer-result">
              <article className="rounded-xl border border-[var(--gold)] bg-white p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--gold)]">Newseze Score</div>
                  {result.media_type && result.media_type !== "text" && (
                    <span className="text-[9.5px] uppercase tracking-[0.14em] font-black bg-[#fff9ed] text-[#7a5b00] border border-[var(--gold)] px-1.5 py-0.5 rounded" data-testid="universal-scorer-media-type">
                      {MEDIA_LABELS[result.media_type] || result.media_type}
                    </span>
                  )}
                </div>
                <h3 className="text-[18px] font-black tracking-[-0.015em] leading-snug mb-2">{result.newseze_title}</h3>
                {result.blocked && (
                  <div className="rounded-md bg-rose-50 border border-rose-200 p-2.5 mb-3 text-[12px] text-rose-800 flex items-start gap-2">
                    <AlertCircle size={13} className="shrink-0 mt-0.5" />
                    <div>
                      <b>Newseze would not republish this.</b>
                      <div>{result.block_reason || "Failed calm filters."}</div>
                    </div>
                  </div>
                )}
                {result.why_it_matters && (
                  <p className="text-[12.5px] font-bold border-l-4 border-[var(--gold)] pl-2.5 italic mb-2 leading-snug">{result.why_it_matters}</p>
                )}
                <p className="text-[13px] text-[var(--ink)] leading-snug whitespace-pre-wrap">{result.narrative}</p>
                {result.source_url && (
                  <a href={result.source_url} target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer" className="text-[11px] text-[var(--blue)] underline mt-3 inline-flex items-center gap-1">
                    <ExternalLink size={11} /> Open source
                  </a>
                )}
                {result.share_id && (
                  <div className="mt-3 pt-3 border-t border-[var(--line)]" data-testid="scorer-share-row">
                    <div className="flex items-center gap-2 mb-2">
                      <Share2 size={12} className="text-[var(--gold)]" />
                      <span className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--muted)]">Share this score</span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Link to={`/scored/${result.share_id}`} className="btn icon" data-testid="scorer-share-permalink">
                        <ExternalLink size={11} /> Permalink
                      </Link>
                      <a
                        href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("Newseze scored this — Trust " + result.trust_score + " · Drama " + result.drama_score + " · Bias " + (result.bias_score >= 0 ? "+" : "") + result.bias_score)}&url=${encodeURIComponent(shareUrl)}`}
                        target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer"
                        className="btn icon" data-testid="scorer-share-x"
                      ><Twitter size={11} /> X</a>
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                        target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer"
                        className="btn icon" data-testid="scorer-share-fb"
                      ><Facebook size={11} /> FB</a>
                      <button onClick={copy} className="btn icon" data-testid="scorer-share-copy">
                        {copied ? <Check size={11} /> : <Copy size={11} />}
                        {copied ? "Copied" : "Copy"}
                      </button>
                    </div>
                  </div>
                )}
              </article>
              <aside className="rounded-xl border border-[var(--line)] bg-white p-4" data-testid="universal-scorer-scores">
                <div className="flex items-center gap-2 mb-2"><Shield size={13} className="text-[var(--gold)]" /><div className="text-[10.5px] uppercase tracking-[0.18em] font-black text-[var(--blue)]">Editorial Scores</div></div>
                <ScoreRow label="Trust" value={result.trust_score} good />
                <ScoreRow label="Respect" value={result.respect_score} good />
                <ScoreRow label="Drama" value={result.drama_score} good={false} />
                <ScoreRow label="Conspiracy" value={result.conspiracy_score} good={false} />
                <div className="mt-2 pt-2 border-t border-[var(--line)]">
                  <div className="text-[9.5px] uppercase tracking-[0.16em] font-black text-[var(--muted)]">Bias</div>
                  <div className="text-[18px] font-black tabular-nums">{result.bias_score > 0 ? `+${result.bias_score}` : result.bias_score}</div>
                  <div className="text-[9px] text-[var(--muted)] italic">-100 left · 0 center · +100 right</div>
                </div>
                {result.quota && (
                  <div className="mt-2 pt-2 border-t border-[var(--line)] text-[10px] text-[var(--muted)]">
                    Today: <b className="text-[var(--ink)]">{result.quota.used}/{result.quota.cap === 9999 ? "∞" : result.quota.cap}</b> · <span className="uppercase tracking-[0.12em] font-black">{result.quota.tier}</span>
                  </div>
                )}
              </aside>
            </div>
          )}
        </div>
      </div>
      {showPricing && <PricingModal onClose={() => setShowPricing(false)} />}
    </div>
  );
}

function ScoreRow({ label, value, good = true }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const color = good ? (pct >= 70 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-500" : "bg-rose-500") : (pct >= 70 ? "bg-rose-500" : pct >= 40 ? "bg-amber-500" : "bg-emerald-500");
  return (
    <div className="mb-1.5">
      <div className="flex justify-between text-[10.5px]"><span className="text-[var(--muted)]">{label}</span><b className="tabular-nums">{value || 0}</b></div>
      <div className="h-1 bg-[var(--bg)] rounded-full overflow-hidden mt-0.5"><div className={`h-full ${color}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}
